import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHRDashboardData } from "@/hooks/useHRDashboardData";
import { Users, Briefcase, CheckCircle2, Clock, AlertCircle, TrendingUp, Calendar, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from "date-fns";
import { vi } from "date-fns/locale";

const HRDashboard = () => {
  const { data: dashboardData, isLoading, error, refetch } = useHRDashboardData();
  const tasks = dashboardData?.tasks || [];
  const members = dashboardData?.members || [];
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<'week' | 'month'>('week');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // BOD can view all members (no auth needed - data comes from Edge Function)
  const canViewAllMembers = true;

  // Calculate stats theo timeFilter
  const stats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { locale: vi });
    const weekEnd = endOfWeek(now, { locale: vi });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Lọc tasks theo tuần/tháng:
    // - Chưa hoàn thành: hiển thị tất cả (kể cả tạo tuần/tháng trước)
    // - Đã hoàn thành: chỉ hiển thị nếu hoàn thành trong tuần/tháng này
    const filteredTasks = tasks.filter(t => {
      const completedDate = t.completed_at ? new Date(t.completed_at) : null;
      
      // Nếu chưa hoàn thành => luôn hiển thị
      if (t.status !== 'completed') {
        return true;
      }
      
      // Nếu đã hoàn thành => chỉ hiển thị nếu hoàn thành trong khoảng thời gian
      if (timeFilter === 'week') {
        return completedDate && completedDate >= weekStart && completedDate <= weekEnd;
      } else {
        return completedDate && completedDate >= monthStart && completedDate <= monthEnd;
      }
    });

    // Total tasks (trong khoảng thời gian)
    const totalTasks = filteredTasks.length;

    // Tasks by status
    const todoTasks = filteredTasks.filter(t => t.status === 'todo').length;
    const inProgressTasks = filteredTasks.filter(t => t.status === 'in_progress').length;
    const completedTasks = filteredTasks.filter(t => t.status === 'completed').length;

    // Overdue tasks (trong khoảng thời gian)
    const overdueTasks = filteredTasks.filter(t => {
      if (t.status === 'completed' || !t.due_date) return false;
      const dueDate = new Date(t.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).length;

    // Completed this week
    const completedThisWeek = tasks.filter(t => {
      if (!t.completed_at || t.status !== 'completed') return false;
      const completedDate = new Date(t.completed_at);
      return completedDate >= weekStart && completedDate <= weekEnd;
    }).length;

    // Completed this month
    const completedThisMonth = tasks.filter(t => {
      if (!t.completed_at || t.status !== 'completed') return false;
      const completedDate = new Date(t.completed_at);
      return completedDate >= monthStart && completedDate <= monthEnd;
    }).length;

    // Completion rate (trong khoảng thời gian)
    const completionRate = totalTasks > 0 
      ? Math.round((completedTasks / totalTasks) * 100) 
      : 0;

    return {
      totalTasks,
      todoTasks,
      inProgressTasks,
      completedTasks,
      overdueTasks,
      completedThisWeek,
      completedThisMonth,
      completionRate,
      totalMembers: members.length,
    };
  }, [tasks, members, timeFilter]);

  // Get member stats - BOD xem tất cả
  const memberStats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { locale: vi });
    const weekEnd = endOfWeek(now, { locale: vi });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    
    return members.map(member => {
      // Lọc tasks theo member và timeFilter
      // - Chưa hoàn thành: hiển thị tất cả (kể cả tạo tuần/tháng trước)
      // - Đã hoàn thành: chỉ hiển thị nếu hoàn thành trong tuần/tháng này
      const memberTasks = tasks.filter(t => {
        if (t.assigned_to !== member.id) return false;
        
        const completedDate = t.completed_at ? new Date(t.completed_at) : null;
        
        // Nếu chưa hoàn thành => luôn hiển thị
        if (t.status !== 'completed') {
          return true;
        }
        
        // Nếu đã hoàn thành => chỉ hiển thị nếu hoàn thành trong khoảng thời gian
        if (timeFilter === 'week') {
          return completedDate && completedDate >= weekStart && completedDate <= weekEnd;
        } else {
          return completedDate && completedDate >= monthStart && completedDate <= monthEnd;
        }
      });
      
      const completed = memberTasks.filter(t => t.status === 'completed').length;
      const inProgress = memberTasks.filter(t => t.status === 'in_progress').length;
      const todo = memberTasks.filter(t => t.status === 'todo').length;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Tính số task quá hạn
      const overdue = memberTasks.filter(t => {
        if (t.status === 'completed' || !t.due_date) return false;
        const dueDate = new Date(t.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      }).length;

      // Tính trung bình giờ và ngày cho các task đã hoàn thành
      const completedTasks = memberTasks.filter(t => t.status === 'completed' && t.time_spent_hours && t.time_spent_hours > 0);
      const avgHoursPerTask = completedTasks.length > 0 
        ? completedTasks.reduce((sum, t) => sum + (t.time_spent_hours || 0), 0) / completedTasks.length 
        : 0;
      const avgDaysPerTask = completedTasks.length > 0 
        ? completedTasks.reduce((sum, t) => sum + (t.time_spent_days || 0), 0) / completedTasks.length 
        : 0;

      return {
        ...member,
        totalTasks: memberTasks.length,
        completed,
        inProgress,
        todo,
        overdue,
        avgHoursPerTask,
        avgDaysPerTask,
      };
    }).sort((a, b) => b.totalTasks - a.totalTasks);
  }, [tasks, members, timeFilter]);


  // Get and sort tasks for expanded member (theo tuần/tháng, bao gồm cả completed)
  const getExpandedMemberTasks = useMemo(() => {
    if (!expandedMemberId) return [];
    
    const now = new Date();
    const weekStart = startOfWeek(now, { locale: vi });
    const weekEnd = endOfWeek(now, { locale: vi });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    
    const memberTasks = tasks
      .filter(t => t.assigned_to === expandedMemberId)
      .filter(t => {
        // Lọc theo tuần/tháng:
        // - Chưa hoàn thành: hiển thị tất cả (kể cả tạo tuần/tháng trước)
        // - Đã hoàn thành: chỉ hiển thị nếu hoàn thành trong tuần/tháng này
        const completedDate = t.completed_at ? new Date(t.completed_at) : null;
        
        // Nếu chưa hoàn thành => luôn hiển thị
        if (t.status !== 'completed') {
          return true;
        }
        
        // Nếu đã hoàn thành => chỉ hiển thị nếu hoàn thành trong khoảng thời gian
        if (timeFilter === 'week') {
          return completedDate && completedDate >= weekStart && completedDate <= weekEnd;
        } else {
          return completedDate && completedDate >= monthStart && completedDate <= monthEnd;
        }
      });
    
    // Sort by status (in_progress first, then todo, then completed), then by due_date
    return memberTasks.sort((a, b) => {
      // Status order
      const statusOrder = { 'in_progress': 0, 'todo': 1, 'completed': 2 };
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;
      
      // Then by due_date
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [tasks, expandedMemberId, timeFilter]);

  // Check if task is overdue
  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate || status === 'completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mb-4" />
        <p className="text-gray-500">Đang tải dữ liệu dashboard nhân sự...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
          <p className="text-gray-500 text-center">
            Lỗi tải dữ liệu dashboard: {error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="mt-4"
          >
            Thử lại
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto py-2 md:py-3 px-2 md:px-4 overflow-x-hidden">
      {/* Header with Time Filter */}
      <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
        <h1 className="text-lg md:text-xl font-bold text-gray-900">Dashboard Tổng Quan - Nhân Sự</h1>
        
        <div className="flex items-center gap-2">
          {/* Time Filter Buttons */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTimeFilter('week')}
              className={cn(
                "px-3 py-1 text-xs md:text-sm font-medium rounded-md transition-colors",
                timeFilter === 'week'
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              Tuần này
            </button>
            <button
              onClick={() => setTimeFilter('month')}
              className={cn(
                "px-3 py-1 text-xs md:text-sm font-medium rounded-md transition-colors",
                timeFilter === 'month'
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              Tháng này
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview - Compact Design */}
      <Card className="mb-2 md:mb-3">
        <CardContent className="p-3 md:p-4">
          {/* Desktop & Tablet: 2 rows x 4 cols */}
          <div className="hidden md:grid md:grid-cols-4 gap-3">
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Tổng CV:</span>
              <span className="text-lg font-bold text-gray-900">{stats.totalTasks}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Đang Làm:</span>
              <span className="text-lg font-bold text-blue-600">{stats.inProgressTasks}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Hoàn Thành:</span>
              <span className="text-lg font-bold text-green-600">{stats.completedTasks}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Quá Hạn:</span>
              <span className="text-lg font-bold text-red-600">{stats.overdueTasks}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">HT Tuần:</span>
              <span className="text-lg font-bold text-emerald-600">{stats.completedThisWeek}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">HT Tháng:</span>
              <span className="text-lg font-bold text-teal-600">{stats.completedThisMonth}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Tỉ Lệ HT:</span>
              <span className="text-lg font-bold text-indigo-600">{`${stats.completionRate}%`}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-gray-600">Thành Viên:</span>
              <span className="text-lg font-bold text-gray-900">{stats.totalMembers}</span>
            </div>
          </div>

          {/* Mobile: 2 columns grid */}
          <div className="md:hidden grid grid-cols-2 gap-2">
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Tổng CV:</span>
              <span className="text-base font-bold text-gray-900">{stats.totalTasks}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Đang Làm:</span>
              <span className="text-base font-bold text-blue-600">{stats.inProgressTasks}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Hoàn Thành:</span>
              <span className="text-base font-bold text-green-600">{stats.completedTasks}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Quá Hạn:</span>
              <span className="text-base font-bold text-red-600">{stats.overdueTasks}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">HT Tuần:</span>
              <span className="text-base font-bold text-emerald-600">{stats.completedThisWeek}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">HT Tháng:</span>
              <span className="text-base font-bold text-teal-600">{stats.completedThisMonth}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Tỉ Lệ HT:</span>
              <span className="text-base font-bold text-indigo-600">{`${stats.completionRate}%`}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-600">Thành Viên:</span>
              <span className="text-base font-bold text-gray-900">{stats.totalMembers}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Member Performance - Responsive */}
      <Card>
        <CardHeader className="pb-3 pt-4">
          <CardTitle className="text-base md:text-lg font-bold">
            {canViewAllMembers ? 'Hiệu Suất Thành Viên' : 'Hiệu Suất Của Tôi'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
            {memberStats.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-xs text-gray-500">Chưa có thành viên nào</p>
            </div>
          ) : (
            <>
              {/* Desktop: Table - Only on very large screens */}
              <div className="hidden xl:block">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Thành Viên</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">TB Giờ/CV</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">TB Ngày/CV</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Tổng</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Chờ</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Đang Làm</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Hoàn Thành</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Trễ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {memberStats.map((member) => {
                      const isExpanded = expandedMemberId === member.id;
                      const memberTasks = isExpanded ? getExpandedMemberTasks : [];
                      
                      return (
                        <React.Fragment key={member.id}>
                          <tr 
                            onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                            className="hover:bg-gray-50 cursor-pointer"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                )}
                                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                  {member.full_name?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900 text-base">{member.full_name || 'N/A'}</span>
                                  {member.is_team_leader && (
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Leader</Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center text-blue-600 font-semibold whitespace-nowrap text-base">
                              {member.avgHoursPerTask > 0 ? `${member.avgHoursPerTask.toFixed(1)}h` : '-'}
                            </td>
                            <td className="px-4 py-3 text-center text-purple-600 font-semibold whitespace-nowrap text-base">
                              {member.avgDaysPerTask > 0 ? `${member.avgDaysPerTask.toFixed(2)}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-base">{member.totalTasks}</td>
                            <td className="px-4 py-3 text-center text-gray-700 font-medium text-base">{member.todo}</td>
                            <td className="px-4 py-3 text-center text-blue-600 font-semibold text-base">{member.inProgress}</td>
                            <td className="px-4 py-3 text-center text-green-600 font-semibold text-base">{member.completed}</td>
                            <td className="px-4 py-3 text-center">
                              {member.overdue > 0 ? (
                                <span className="text-red-600 font-semibold text-base">{member.overdue}</span>
                              ) : (
                                <span className="text-gray-400 text-base">-</span>
                              )}
                            </td>
                          </tr>
                          
                          {/* Expanded Task List */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} className="px-3 py-2 bg-gray-50">
                                {memberTasks.length === 0 ? (
                                  <p className="text-xs text-gray-500 py-2 text-center">Không có công việc</p>
                                ) : (
                                  <div className="ml-10 overflow-x-auto">
                                    <table className="w-full text-xs border border-gray-200">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="px-2 py-1.5 text-left font-medium text-gray-700 border-b">Công việc</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Trạng thái</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Deadline</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Số ngày trễ</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Ngày bắt đầu</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Ngày xong</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Tổng giờ</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-gray-700 border-b whitespace-nowrap">Tổng ngày</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white">
                                        {memberTasks.map(task => (
                                          <tr key={task.id} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="px-2 py-1.5">
                                              <div className="font-medium text-gray-900 line-clamp-1">{task.title}</div>
                                              {task.description && (
                                                <div className="text-gray-500 line-clamp-1 mt-0.5">{task.description}</div>
                                              )}
                                            </td>
                                            <td className="px-2 py-1.5 text-center">
                                              <Badge 
                                                className={cn(
                                                  "text-xs font-medium whitespace-nowrap",
                                                  task.status === 'completed' && "bg-green-500 text-white hover:bg-green-600",
                                                  task.status === 'in_progress' && "bg-yellow-500 text-white hover:bg-yellow-600",
                                                  task.status === 'todo' && "bg-red-500 text-white hover:bg-red-600"
                                                )}
                                              >
                                                {task.status === 'completed' ? 'Xong' : task.status === 'in_progress' ? 'Đang làm' : 'Chờ làm'}
                                              </Badge>
                                            </td>
                                            <td className={cn(
                                              "px-2 py-1.5 text-center whitespace-nowrap",
                                              isOverdue(task.due_date, task.status) ? "text-red-600 font-semibold" : "text-gray-700"
                                            )}>
                                              {task.due_date ? format(new Date(task.due_date), 'dd/MM/yyyy', { locale: vi }) : '-'}
                                            </td>
                                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                                              {(() => {
                                                if (task.status === 'completed' || !task.due_date) return <span className="text-gray-400">-</span>;
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const dueDate = new Date(task.due_date);
                                                dueDate.setHours(0, 0, 0, 0);
                                                if (dueDate < today) {
                                                  const diffTime = today.getTime() - dueDate.getTime();
                                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                                  return <span className="text-red-600 font-bold">{diffDays}</span>;
                                                }
                                                return <span className="text-gray-400">-</span>;
                                              })()}
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-gray-600 whitespace-nowrap">
                                              {task.started_at 
                                                ? format(new Date(task.started_at), 'dd/MM/yy HH:mm', { locale: vi })
                                                : task.created_at 
                                                ? format(new Date(task.created_at), 'dd/MM/yy HH:mm', { locale: vi })
                                                : '-'}
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-gray-600 whitespace-nowrap">
                                              {task.completed_at ? format(new Date(task.completed_at), 'dd/MM/yy HH:mm', { locale: vi }) : '-'}
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-green-600 font-medium whitespace-nowrap">
                                              {task.time_spent_hours ? `${task.time_spent_hours.toFixed(1)}h` : '-'}
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-green-600 font-medium whitespace-nowrap">
                                              {task.time_spent_days ? `${task.time_spent_days.toFixed(2)} ngày` : '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet/Laptop: Card List */}
              <div className="xl:hidden divide-y">
                {memberStats.map((member) => {
                  const isExpanded = expandedMemberId === member.id;
                  const memberTasks = isExpanded ? getExpandedMemberTasks : [];
                  
                  return (
                    <div key={member.id} className="p-2 md:p-3">
                      <div 
                        onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                        className="cursor-pointer"
                      >
                        {/* Header: Icon + Name + Leader Badge + Performance Badge + Total */}
                        <div className="flex items-center gap-2 mb-2">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          )}
                          <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm md:text-base font-semibold flex-shrink-0">
                            {member.full_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                            <span className="text-base md:text-lg font-semibold text-gray-900">{member.full_name || 'N/A'}</span>
                            {member.is_team_leader && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Leader</Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="text-sm md:text-base px-2 py-1 font-bold">{member.totalTasks}</Badge>
                        </div>
                        
                        {/* Stats: Label: Value on same line */}
                        <div className="ml-8 md:ml-10 space-y-1 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">TB Giờ/CV:</span>
                              <span className="font-semibold text-blue-600">
                                {member.avgHoursPerTask > 0 ? `${member.avgHoursPerTask.toFixed(1)}h` : '-'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">TB Ngày/CV:</span>
                              <span className="font-semibold text-purple-600">
                                {member.avgDaysPerTask > 0 ? member.avgDaysPerTask.toFixed(2) : '-'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 md:gap-6">
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-600">Chờ:</span>
                              <span className="font-semibold text-gray-700">{member.todo}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-600">Đang làm:</span>
                              <span className="font-semibold text-blue-600">{member.inProgress}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-600">Xong:</span>
                              <span className="font-semibold text-green-600">{member.completed}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-600">Trễ:</span>
                              <span className="font-semibold text-red-600">
                                {member.overdue > 0 ? member.overdue : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Task List */}
                      {isExpanded && (
                        <div className="mt-1.5 ml-6 overflow-x-auto">
                          {memberTasks.length === 0 ? (
                            <p className="text-xs text-gray-500 py-1.5 text-center">Không có công việc</p>
                          ) : (
                            <table className="w-full text-xs border border-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-1.5 py-1 text-left font-medium text-gray-700 border-b text-xs">Công việc</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">TT</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">DL</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">Trễ</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">Bắt đầu</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">Xong</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">Giờ</th>
                                  <th className="px-1.5 py-1 text-center font-medium text-gray-700 border-b whitespace-nowrap text-xs">Ngày</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white">
                                {memberTasks.map(task => (
                                  <tr key={task.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="px-1.5 py-1">
                                      <div className="font-medium text-gray-900 line-clamp-1 text-xs">{task.title}</div>
                                      {task.description && (
                                        <div className="text-gray-500 line-clamp-1 mt-0.5 text-xs">{task.description}</div>
                                      )}
                                    </td>
                                    <td className="px-1.5 py-1 text-center">
                                      <Badge 
                                        className={cn(
                                          "text-[10px] font-medium whitespace-nowrap px-1 py-0",
                                          task.status === 'completed' && "bg-green-500 text-white hover:bg-green-600",
                                          task.status === 'in_progress' && "bg-yellow-500 text-white hover:bg-yellow-600",
                                          task.status === 'todo' && "bg-red-500 text-white hover:bg-red-600"
                                        )}
                                      >
                                        {task.status === 'completed' ? 'Xong' : task.status === 'in_progress' ? 'Làm' : 'Chờ làm'}
                                      </Badge>
                                    </td>
                                    <td className={cn(
                                      "px-1.5 py-1 text-center whitespace-nowrap text-xs",
                                      isOverdue(task.due_date, task.status) ? "text-red-600 font-semibold" : "text-gray-700"
                                    )}>
                                      {task.due_date ? format(new Date(task.due_date), 'dd/MM', { locale: vi }) : '-'}
                                    </td>
                                    <td className="px-1.5 py-1 text-center whitespace-nowrap text-xs">
                                      {(() => {
                                        if (task.status === 'completed' || !task.due_date) return <span className="text-gray-400">-</span>;
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const dueDate = new Date(task.due_date);
                                        dueDate.setHours(0, 0, 0, 0);
                                        if (dueDate < today) {
                                          const diffTime = today.getTime() - dueDate.getTime();
                                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                          return <span className="text-red-600 font-bold">{diffDays}</span>;
                                        }
                                        return <span className="text-gray-400">-</span>;
                                      })()}
                                    </td>
                                    <td className="px-1.5 py-1 text-center text-gray-600 whitespace-nowrap text-xs">
                                      {task.started_at 
                                        ? format(new Date(task.started_at), 'dd/MM', { locale: vi })
                                        : task.created_at 
                                        ? format(new Date(task.created_at), 'dd/MM', { locale: vi })
                                        : '-'}
                                    </td>
                                    <td className="px-1.5 py-1 text-center text-gray-600 whitespace-nowrap text-xs">
                                      {task.completed_at ? format(new Date(task.completed_at), 'dd/MM', { locale: vi }) : '-'}
                                    </td>
                                    <td className="px-1.5 py-1 text-center text-green-600 font-medium whitespace-nowrap text-xs">
                                      {task.time_spent_hours ? `${task.time_spent_hours.toFixed(1)}h` : '-'}
                                    </td>
                                    <td className="px-1.5 py-1 text-center text-green-600 font-medium whitespace-nowrap text-xs">
                                      {task.time_spent_days ? `${task.time_spent_days.toFixed(1)}` : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HRDashboard;

